import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { Badge } from '../../components/ui/badge';
import { FileText, MessageSquare, User, Settings, BarChart3, Building2, Stamp, FolderOpen, Calculator, Video, Home, Wallet, UserPlus, Users } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../contexts/AuthContext';
import { useClickTracking } from '../../contexts/ClickTrackingContext';
import axios from 'axios';

import { toast } from 'sonner';

import { getToken } from '../../services/tokenService';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

export function useExpertDashboard() {
  const { t: t } = useTranslation();
  const { user, logout } = useAuth();
  const { trackClick: trackClick } = useClickTracking();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');
  const [loading, setLoading] = useState(true);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  
  // États
  const [dommages, setDommages] = useState([]);
  const [stats, setStats] = useState({});
  const [expertProfile, setExpertProfile] = useState(null);
  const [_selectedDommage, setSelectedDommage] = useState(null);
  const [filterStatus, setFilterStatus] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');

  useEffect(() => {
    if (user?.role === 'expert' && user?.statut === 'validé') {
      fetchDashboardData();
    } else if (user?.role === 'expert' && user?.statut !== 'validé') {
      // Compte non validé
      setActiveTab('en-attente');
      setLoading(false);
    } else {
      navigate('/login');
    }
  }, [user, navigate]);

  const getAuthHeader = () => ({
    Authorization: `Bearer ${getToken()}`
  });

  const fetchDashboardData = async () => {
    setLoading(true);
    try {
      const [dommagesRes, statsRes, profileRes] = await Promise.all([
        axios.get(`${API}/dommages`, { headers: getAuthHeader() }),
        axios.get(`${API}/dommages/stats/dashboard`, { headers: getAuthHeader() }),
        axios.get(`${API}/experts/${user.id}`, { headers: getAuthHeader() })
      ]);

      setDommages(dommagesRes.data || []);
      setStats(statsRes.data || {});
      setExpertProfile(profileRes.data);
    } catch {
      toast.error('Erreur lors du chargement des données');
    } finally {
      setLoading(false);
    }
  };

  const handleLogout = () => {
    logout();
    window.location.href = '/';
  };

  const getStatutBadge = (statut) => {
    const config = {
      'déclaré': { color: 'bg-blue-700 text-white', icon: '📋' },
      'en_cours_instruction': { color: 'bg-yellow-600 text-white', icon: '⏳' },
      'expertise_en_cours': { color: 'bg-purple-700 text-white', icon: '🔍' },
      'en_attente_pièces': { color: 'bg-orange-600 text-white', icon: '📎' },
      'traité': { color: 'bg-green-700 text-white', icon: '✅' },
      'clos': { color: 'bg-gray-600 text-white', icon: '🔒' },
      'rejeté': { color: 'bg-red-700 text-white', icon: '❌' }
    };
    
    const statusConfig = config[statut] || config['déclaré'];
    return (
      <Badge className={`${statusConfig.color} border`}>
        {statusConfig.icon} {statut?.replace('_', ' ')}
      </Badge>
    );
  };

  const getTypeBadge = (type) => {
    const config = {
      'décennale': { color: 'bg-red-700 text-white border-red-900', icon: '🛡️' },
      'bon_fonctionnement': { color: 'bg-blue-700 text-white border-blue-900', icon: '⚙️' },
      'parfait_achèvement': { color: 'bg-green-700 text-white border-green-900', icon: '✨' },
      'autre': { color: 'bg-gray-600 text-white border-gray-200', icon: '📄' }
    };
    
    const typeConfig = config[type] || config['autre'];
    return (
      <Badge className={`${typeConfig.color} border`}>
        {typeConfig.icon} {type?.replace('_', ' ')}
      </Badge>
    );
  };

  const filteredDommages = dommages.filter(s => {
    const matchesStatus = filterStatus === 'all' || s.statut === filterStatus;
    const matchesSearch = !searchQuery || 
      s.numero_dommage?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.raison_sociale?.toLowerCase().includes(searchQuery.toLowerCase()) ||
      s.objet?.toLowerCase().includes(searchQuery.toLowerCase());
    return matchesStatus && matchesSearch;
  });

  const sidebarTabs = [
    { value: 'wallet-credits', label: 'Wallet & Crédits ESS', icon: <Wallet className="h-5 w-5 text-emerald-600" />, highlight: true },
    { value: 'ftpe-membres', label: 'FTPE Membres', icon: <UserPlus className="h-5 w-5 text-blue-500" />, highlight: true },
    { value: 'mes-membres', label: 'Mes Membres', icon: <Users className="h-5 w-5 text-blue-600" />, highlight: true },
    { value: 'membres-confies', label: 'Membres confiés', icon: <Users className="h-5 w-5 text-indigo-600" />, highlight: true },
    { value: 'simulateur', label: 'Garantie RCDS-BATICO', icon: <Calculator className="h-5 w-5" />, highlight: true },
    { value: 'simulateurs-separator', label: '🎯 SIMULATEURS MAÎTRES', icon: null, isSeparator: true, isHighlight: true },
    { value: 'simulateur-dos', label: 'DOS-DOMIUM', icon: <Home className="h-5 w-5 text-orange-500" /> },
    { value: 'simulateur-ccmi', label: 'CCMI-CAP BÂTI', icon: <Building2 className="h-5 w-5 text-blue-500" /> },
    { value: 'dashboard', label: 'Tableau de bord', icon: <BarChart3 className="h-5 w-5" /> },
    { value: 'dommages', label: 'Mes Dommages', icon: <FileText className="h-5 w-5" /> },
    { value: 'ged-universelle', label: 'GED Universelle', icon: <FolderOpen className="h-5 w-5 text-blue-600" /> },
    { value: 'ged-domium', label: 'GED DOMIUM', icon: <FolderOpen className="h-5 w-5 text-emerald-600" /> },
    { value: 'conventions', label: 'Mes Conventions', icon: <FileText className="h-5 w-5" /> },
    { value: 'signature', label: 'Signature/Tampon', icon: <Stamp className="h-5 w-5" /> },
    { value: 'modeles', label: 'Modèles Documents', icon: <FolderOpen className="h-5 w-5" /> },
    { value: 'videos', label: 'Vidéos', icon: <Video className="h-5 w-5 text-purple-600" /> },
    { value: 'messagerie', label: 'Messagerie', icon: <MessageSquare className="h-5 w-5" /> },
    { value: 'profil', label: 'Mon Profil', icon: <User className="h-5 w-5" /> },
    { value: 'parametres', label: 'Paramètres', icon: <Settings className="h-5 w-5" /> },
  ];

  return { t, user, logout, trackClick, navigate, activeTab, setActiveTab, loading, setLoading, sidebarOpen, setSidebarOpen, dommages, setDommages, stats, setStats, expertProfile, setExpertProfile, _selectedDommage, setSelectedDommage, filterStatus, setFilterStatus, searchQuery, setSearchQuery, getAuthHeader, fetchDashboardData, handleLogout, getStatutBadge, getTypeBadge, filteredDommages, sidebarTabs };
}
